import { Directions as DirectionsIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { addUrlProtocol, validateUrl } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TransportModes } from '@nx-smartmonkey/shared/domain';
import IconStopClock from '../../../../../../../assets/img/stop-time-counter/icon-stop-clock.svg';
import IconStopPause from '../../../../../../../assets/img/stop-time-counter/icon-stop-pause.svg';
import EditIcon from '../../../../../../../assets/svg/EditIcon';
import PhoneIcon from '../../../../../../../assets/svg/PhoneIcon';
import SkipIcon from '../../../../../../../assets/svg/SkipIcon';
import WebIcon from '../../../../../../../assets/svg/WebIcon';
import { ROUTE_END_LOCATION, ROUTE_START_LOCATION } from '../../../../../../../constants/Routes';
import * as routing from '../../../../../../../constants/Routing';
import { RouteStatus } from '../../../../../../../domain/route/RouteStatus';
import { Stop } from '../../../../../../../domain/stops/Stop';
import { intl } from '../../../../../../../helpers/IntGlobalProvider';
import { MapsApplicationTypes } from '../../../../../../../interfaces/MapsApplication';
import Button from '../../../../../../../react-components/Button';
import { Text } from '../../../../../../../react-components/Text';
import {
  LocalNotificationActionEnum,
  LocalNotificationEnum,
  routalLocalNotifications,
} from '../../../../../../../services/localNotifications';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { useRetrieveVehicle } from '../../../../../hooks/useRetrieveVehicle';
import { RouteActionType, RouteActions } from '../../../../context/actions';
import { useRouteContext } from '../../../../context/context';
import { useStartStop } from '../../../../hooks/useStartStop';
import { CounterTimeText } from '../../CounterTimeText';
import { EditStartOrEndLocation } from './EditStartOrEndLocation';

export const openNavigationLink = (
  lat: number,
  lng: number,
  mapsApplication: MapsApplicationTypes,
  vehicleType: TransportModes = `car` // Default to 'car'
) => {
  const url =
    mapsApplication === `google_maps`
      ? `https://maps.google.com/maps/dir/?api=1&origin=My+Location&destination=${lat},${lng}&travelmode=${getGoogleTransportModeFlag(
          vehicleType
        )}` // https://developers.google.com/maps/documentation/urls/get-started
      : mapsApplication === `apple_maps`
      ? `http://maps.apple.com/?daddr=${lat},${lng}&dirflg=${
          vehicleType === `pedestrian` ? `w` : vehicleType === `bicycle` ? `c` : `d`
        }
` //https://developer.apple.com/library/archive/featuredarticles/iPhoneURLScheme_Reference/MapLinks/MapLinks.html
      : `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;

  window.open(url, `_system`);
};

const getGoogleTransportModeFlag = (vehicleType: TransportModes) => {
  switch (vehicleType) {
    case `bicycle`:
      return `bicycling`;
    case `pedestrian`:
      return `walking`; // Also transit if we want to use public transport
    case `scooter`: //  `two-wheeler` exist in the docu, but it seems not to work
    case `car`:
    case `truck`:
    default:
      return `driving`;
  }
};

interface ActionButtonProps {
  label?: string;
  image: string | React.ReactNode;
  filled?: boolean;
  onClick: () => void;
}

const ActionButton = ({ label, image, filled = false, onClick }: ActionButtonProps) => {
  return (
    <>
      <Button
        style={{
          background: filled ? RoutalPalette.primary40 : RoutalPalette.white,
          display: `flex`,
          alignItems: `center`,
          padding: `8px 20px`,
          borderRadius: `10px`,
          border: `1px ${RoutalPalette.primary40} solid`,
          width: `fit-content`,
          height: `auto`,
        }}
        onClick={onClick}
      >
        {typeof image === `string` ? <img src={image} alt={label} style={{ width: `19px`, height: `19px` }} /> : image}
        {label && (
          <Text
            style={{
              marginLeft: `8px`,
              color: filled ? RoutalPalette.white : RoutalPalette.primary40,
              fontSize: `13px`,
              whiteSpace: `nowrap`,
            }}
          >
            {label}
          </Text>
        )}
      </Button>
      <HSpacer small />
    </>
  );
};

interface ActionProps {
  label?: string;
  image: string | React.ReactNode;
  filled?: boolean;
  onClick: () => void;
}

interface generateActionsProps {
  stop: Stop;
  routeStatus: RouteStatus;
  onlyOneStopLeft?: boolean;
  isStopCounting: boolean;
  canManageStops: boolean;
  canReorderStops: boolean;
  canManageRoutes: boolean;
  routeDispatch: React.Dispatch<RouteActions>;
  openMapsModal?: () => void;
  openSkipModal?: () => void;
  openEditStopLocation?: () => void;
  mapsApplication?: MapsApplicationTypes;
  vehicleType?: TransportModes;
  navigate?: (url: string) => void;
  isStartOrEndStop: boolean;
}

const generateActions = ({
  stop,
  routeStatus,
  onlyOneStopLeft,
  isStopCounting,
  canManageStops,
  canReorderStops,
  canManageRoutes,
  routeDispatch,
  openMapsModal,
  openSkipModal,
  openEditStopLocation,
  navigate,
  mapsApplication,
  vehicleType,
  isStartOrEndStop,
}: generateActionsProps) => {
  const actions: ActionProps[] = [];
  // Navigation action
  if (stop.location?.isValid()) {
    actions.push({
      label: intl.formatMessage({ id: `drivers.action_button.directions` }),
      image: <DirectionsIcon />,
      filled: true,
      onClick: () => {
        if (!mapsApplication) {
          openMapsModal && openMapsModal();
        } else {
          openNavigationLink(stop.location!.lat!, stop.location!.lng!, mapsApplication, vehicleType); // we can use ! since we check if lat and lng exist

          if (stop.location!.label)
            routalLocalNotifications.scheduleNotification(
              {
                title: `${stop.location?.label}`,
                body: stop.label ?? ``,
                group: `Navigation`,
                // schedule: { at: new Date(Date.now() + 1000 * 20) },
                sound: `none`,
                actionTypeId: LocalNotificationEnum.ROUTE,
                id: 1,
              },
              (notification) => {
                if (navigate && stop.route_id) {
                  if (notification.actionId === LocalNotificationActionEnum.COMPLETE) {
                    navigate(
                      routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, stop.route_id)
                        .replace(`:stopId`, stop.id)
                        .concat(`/complete`)
                    );
                  }
                  if (notification.actionId === LocalNotificationActionEnum.CANCEL) {
                    navigate(
                      routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, stop.route_id)
                        .replace(`:stopId`, stop.id)
                        .concat(`/cancel`)
                    );
                  }
                }
              }
            );
        }
      },
    });
  }

  // Phone action
  if (stop.stop_fields && stop.stop_fields.some((field) => field.id === `phone`)) {
    const phoneField = stop.stop_fields.find((field) => field.id === `phone`);
    if (phoneField && phoneField.value) {
      actions.push({
        //label: intl.formatMessage({ id: `drivers.action_button.phone_call` }),
        image: <PhoneIcon />, // No padding since they dont use label style={{ paddingRight: `8px` }}
        onClick: () => {
          window.location.href = `tel:${phoneField.value?.replace(/ /g, ``)}`;
        },
      });
    }
  }

  // Website action
  if (stop.stop_fields && stop.stop_fields.some((field) => field.id === `url`)) {
    const websiteField = stop.stop_fields.find((field) => field.id === `url`);
    if (validateUrl(websiteField?.value ?? ``)) {
      actions.push({
        //label: intl.formatMessage({ id: `driver.stop.entry.url` }),
        image: <WebIcon />, // No padding since they don't use label style={{ paddingRight: `8px` }}
        onClick: () => {
          window.open(addUrlProtocol(websiteField!.value), `_blank`);
        },
      });
    }
  }

  if (canReorderStops && routeStatus === `in_transit` && !isStopCounting && !onlyOneStopLeft && !isStartOrEndStop) {
    actions.push({
      label: intl.formatMessage({ id: `drivers.action_button.skip` }),
      image: <SkipIcon />,
      onClick: () => {
        openSkipModal && openSkipModal();
      },
    });
  }

  if (canManageStops && routeStatus !== `finished` && !isStartOrEndStop) {
    actions.push({
      label: intl.formatMessage({ id: `drivers.action_button.edit` }),
      image: <EditIcon />,
      onClick: () => {
        routeDispatch({ type: RouteActionType.SET_EDITING_STOP, payload: stop });
      },
    });
  }

  if (canManageRoutes && routeStatus !== `finished` && isStartOrEndStop) {
    actions.push({
      label: intl.formatMessage({ id: `drivers.action_button.edit_location` }),
      image: <EditIcon />,
      onClick: () => {
        openEditStopLocation && openEditStopLocation();
      },
    });
  }

  // if (stop.stop_fields && stop.stop_fields.some((field) => field.id === `email`)) {
  //   const emailField = stop.stop_fields.find((field) => field.id === `email`);
  //   if (emailField && emailField.value) {
  //     actions.push({
  //       label: intl.formatMessage({ id: `driver.stop.entry.email` }),
  //       image: <EmailIcon style={{ paddingRight: `8px` }} />,
  //       onClick: () => {
  //         window.location.href = `mailto:${emailField?.value}`;
  //       },
  //     });
  //   }
  // }

  return actions;
};

export type ActionButtonTypes = `stop_counting`;

interface ActionButtonsProps {
  stop: Stop;
  routeStatus: RouteStatus;
  onlyOneStopLeft?: boolean;
  style?: React.CSSProperties;
  mapsApplication?: MapsApplicationTypes;
  onTriggerAction?: (action: ActionButtonTypes) => void;
  openMapsModal: () => void;
  openSkipModal?: () => void;
}

export const ActionButtons = ({
  stop,
  onlyOneStopLeft = true,
  routeStatus,
  style,
  mapsApplication,
  onTriggerAction,
  openMapsModal,
  openSkipModal,
}: ActionButtonsProps) => {
  const { canManageStops, canReorderStops, canManageRoutes } = usePermissions();
  const { data: vehicleData } = useRetrieveVehicle();
  const { timeInStop: counter, startedStopId } = useStartStop();
  const { routeDispatch } = useRouteContext();

  const [openEditAddress, setOpenEditAddress] = useState(false);
  const navigate = useNavigate();

  const actions: ActionProps[] = useMemo(
    () =>
      generateActions({
        stop,
        isStopCounting: startedStopId === stop.id,
        onlyOneStopLeft,
        routeStatus,
        canManageStops,
        canReorderStops,
        canManageRoutes,
        routeDispatch,
        mapsApplication,
        vehicleType: vehicleData?.project.transport_mode,
        openSkipModal,
        navigate,
        openMapsModal,
        isStartOrEndStop: stop.id === ROUTE_START_LOCATION || stop.id === ROUTE_END_LOCATION,
        openEditStopLocation: () => {
          setOpenEditAddress(true);
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stop, canManageStops, canReorderStops, canManageRoutes, startedStopId, onlyOneStopLeft, mapsApplication]
  );

  return (
    <>
      <Grid style={{ width: `100%`, overflowY: `scroll`, scrollbarWidth: `none`, ...style }}>
        <Grid style={{ display: `flex`, flexDirection: `row`, width: `fit-content` }}>
          {startedStopId === stop.id && vehicleData?.project.fields.configuration.includes(`stop_duration`) && (
            <Button
              style={{
                backgroundColor: RoutalPalette.primary20n,
                marginRight: `10px`,
                borderRadius: `10px`,
                color: RoutalPalette.primary40,
                height: `auto`,
                display: `flex`,
                flexWrap: `nowrap`,
                padding: `8px 15px`,
                alignItems: `center`,
                justifyContent: `space-between`,
                width: `100px`,
              }}
              onClick={() => {
                onTriggerAction && onTriggerAction(`stop_counting`);
              }}
            >
              <Grid item style={{ display: `flex`, alignItems: `center` }}>
                <img src={IconStopClock} alt="" width="12" />
                <CounterTimeText counter={counter ?? 0} />
              </Grid>
              <HSpacer small />
              <img src={IconStopPause} alt="" width="20" />
            </Button>
          )}

          {actions.map((action) => (
            <ActionButton
              key={action.label}
              label={action.label}
              image={action.image}
              filled={action.filled}
              onClick={action.onClick}
            />
          ))}
        </Grid>
      </Grid>
      {(stop.id === ROUTE_START_LOCATION || stop.id === ROUTE_END_LOCATION) && stop.status !== `completed` && (
        <EditStartOrEndLocation stop={stop} isOpen={openEditAddress} close={() => setOpenEditAddress(false)} />
      )}
    </>
  );
};
